import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import aspectRatios, { AspectRatioIcon } from '../../constants/aspectRatios';
import resolutionMap, { RESOLUTION_OPTIONS } from '../../constants/resolutionMap';
import { generateSketchToInterior } from '../../services/geminiService';
import { generateWithFlux, isFluxModel } from '../../services/fluxService';
import { useAuth } from '../../contexts/AuthContext';
import {
  projectsService,
  savedDesignsService,
  generationMetadataService,
  userProfilesService,
  storageService,
} from '../../services/supabaseService';
import { supabase } from '../../lib/supabase';
import { useProjects } from '../../contexts/ProjectContext';
import toast, { Toaster } from 'react-hot-toast';
import UploadedImageThumb from '../../components/UploadedImageThumb';
import ModelSelectionDropdown from '../../components/ModelSelectionDropdown';
import AssetLibraryPanel from '../../components/AssetLibraryPanel';
import RoomTypeDropdown from '../../components/RoomTypeDropdown';
import GenerationStatusNotification from '../../components/GenerationStatusNotification';
import useGenerationTimeout from '../../hooks/useGenerationTimeout';
import { getModelCreditCost } from '../../utils/creditCost';
import { checkSaveDesignLimit } from '../../utils/planLimits';
import { downloadImage } from '../../utils/downloadUtils';

const SketchToInteriorRender = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { showSaveModal } = useProjects();
  const { user, userProfile, loading, profileLoading } = useAuth();
  const [sketchImages, setSketchImages] = useState([]);
  const [moodImages, setMoodImages] = useState([]);
  const [selectedModel, setSelectedModel] = useState('Gemini 3.1 Flash Image Preview');
  const [selectedImageSize, setSelectedImageSize] = useState('Auto');
  const [prompt, setPrompt] = useState('');
  const [currentImage, setCurrentImage] = useState(null);
  const [sessionLibraryImages, setSessionLibraryImages] = useState([]);
  const [selectedRoomType, setSelectedRoomType] = useState('--');
  const [wallsMaterial, setWallsMaterial] = useState([]);
  const [sofasMaterial, setSofasMaterial] = useState([]);
  const [ceilingMaterial, setCeilingMaterial] = useState([]);
  const [floorMaterial, setFloorMaterial] = useState([]);
  const [libraryAssets, setLibraryAssets] = useState([]);
  const [currentProjectId, setCurrentProjectId] = useState(null);
  const [buttonLoading, setButtonLoading] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationError, setGenerationError] = useState('');
  const [isAspectRatioOpen, setIsAspectRatioOpen] = useState(false);
  const [selectedResolution, setSelectedResolution] = useState('1K');
  const [isResolutionOpen, setIsResolutionOpen] = useState(false);
  const promptRef = useRef(null);
  const aspectRatioRef = useRef(null);
  const resolutionRef = useRef(null);
  const aspectRatioMobileRef = useRef(null);
  const resolutionMobileRef = useRef(null);
  const abortControllerRef = useRef(null);
  const isGeneratingRef = useRef(false);

  // Virtual Tool Guide state
  const [showGuide, setShowGuide] = useState(false);
  const [guideStep, setGuideStep] = useState(1);
  const [guideAnimating, setGuideAnimating] = useState(false);
  const [guideVisible, setGuideVisible] = useState(false);
  const guideBubblePosRef = useRef({ top: '50%', left: '50%' });

  const sketchUploadDesktopRef = useRef(null);
  const sketchUploadMobileRef = useRef(null);
  const moodUploadDesktopRef = useRef(null);
  const moodUploadMobileRef = useRef(null);
  const roomTypeDesktopRef = useRef(null);
  const roomTypeMobileRef = useRef(null);
  const materialsDesktopRef = useRef(null);
  const materialsMobileRef = useRef(null);
  const assetLibraryDesktopRef = useRef(null);
  const assetLibraryMobileRef = useRef(null);
  const modelSelectionDesktopRef = useRef(null);
  const modelSelectionMobileRef = useRef(null);
  const aspectRatioGlowDesktopRef = useRef(null);
  const aspectRatioGlowMobileRef = useRef(null);
  const resolutionGlowDesktopRef = useRef(null);
  const resolutionGlowMobileRef = useRef(null);
  const promptDesktopRef = useRef(null);
  const promptMobileRef = useRef(null);
  const generateDesktopRef = useRef(null);
  const generateMobileRef = useRef(null);
  const resultDesktopRef = useRef(null);
  const resultMobileRef = useRef(null);

  const totalGuideSteps = 11;

  const scrollGuideTarget = (step) => {
    const desktopRefs = [sketchUploadDesktopRef, moodUploadDesktopRef, roomTypeDesktopRef, materialsDesktopRef, assetLibraryDesktopRef, modelSelectionDesktopRef, aspectRatioGlowDesktopRef, resolutionGlowDesktopRef, promptDesktopRef, generateDesktopRef, resultDesktopRef];
    const mobileRefs = [sketchUploadMobileRef, moodUploadMobileRef, roomTypeMobileRef, materialsMobileRef, assetLibraryMobileRef, modelSelectionMobileRef, aspectRatioGlowMobileRef, resolutionGlowMobileRef, promptMobileRef, generateMobileRef, resultMobileRef];
    const ref = window.innerWidth >= 768 ? desktopRefs[step - 1]?.current : mobileRefs[step - 1]?.current;
    if (ref) {
      ref.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  const openGuide = () => {
    setGuideStep(1);
    setShowGuide(true);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => setGuideVisible(true));
    });
  };

  const nextGuideStep = () => {
    setGuideAnimating(true);
    setGuideVisible(false);
    const nextStep = guideStep + 1;
    const needsScroll = nextStep >= 3;
    if (needsScroll) {
      scrollGuideTarget(nextStep);
    }
    setTimeout(() => {
      setGuideStep(nextStep);
      setGuideAnimating(false);
      if (needsScroll) {
        setTimeout(() => {
          requestAnimationFrame(() => {
            requestAnimationFrame(() => setGuideVisible(true));
          });
        }, 400);
      } else {
        setTimeout(() => {
          requestAnimationFrame(() => {
            requestAnimationFrame(() => setGuideVisible(true));
          });
        }, 100);
      }
    }, 700);
  };

  const closeGuide = () => {
    setGuideVisible(false);
    setTimeout(() => {
      setShowGuide(false);
      setGuideStep(1);
    }, 600);
  };

  const { generationStatus, startTimer, clearTimer, setFailed, setCancelled, dismissNotification } = useGenerationTimeout();

  const MAX_PROMPT_LENGTH = 1600;
  const MAX_UPLOADS = 6;

  const totalUploads = sketchImages.length + moodImages.length + wallsMaterial.length + sofasMaterial.length + ceilingMaterial.length + floorMaterial.length;

  // Get image dimensions based on selected size (matching moodboard tool dimensions)
  const getImageDimensions = () => {
    const ratioMap = resolutionMap[selectedImageSize];
    if (ratioMap && ratioMap[selectedResolution]) {
      return ratioMap[selectedResolution];
    }
    return resolutionMap['Auto']?.[selectedResolution] || { width: 1024, height: 1024 };
  };

  // Models constant removed - using shared ModelSelectionDropdown component
  const imageSizes = ['Auto', '1:1', '4:3', '16:9', '21:9', '5:4', '3:2', '2:3', '9:16', '3:4', '4:5'];
  const roomTypes = [
  '--',
  'Bedroom',
  'Living room',
  'Meeting room',
  'Bathroom',
  'Cabinet',
  'Kitchen',
  'Reception',
  'Lounge',
  'Bar',
  'Terrace',
  'Co-working area',
  'Restaurant'];


  const handleSketchUpload = async (e) => {
    const files = Array.from(e?.target?.files);
    const totalCurrent = sketchImages.length + moodImages.length + wallsMaterial.length + sofasMaterial.length + ceilingMaterial.length + floorMaterial.length;
    const remaining = Math.max(0, MAX_UPLOADS - totalCurrent);
    if (remaining === 0) return;
    const limitedFiles = files.slice(0, remaining);
    const imageUrls = (await Promise.all(limitedFiles.map((file) => storageService.uploadFile(file, user?.id)))).filter(Boolean);
    setSketchImages([...sketchImages, ...imageUrls]);
  };

  const handleMoodUpload = async (e) => {
    const files = Array.from(e?.target?.files);
    const totalCurrent = sketchImages.length + moodImages.length + wallsMaterial.length + sofasMaterial.length + ceilingMaterial.length + floorMaterial.length;
    const remaining = Math.max(0, MAX_UPLOADS - totalCurrent);
    if (remaining === 0) return;
    const limitedFiles = files.slice(0, remaining);
    const imageUrls = (await Promise.all(limitedFiles.map((file) => storageService.uploadFile(file, user?.id)))).filter(Boolean);
    setMoodImages([...moodImages, ...imageUrls]);
  };

  const handleMaterialUpload = async (e, materialType) => {
    const files = Array.from(e?.target?.files);
    const totalCurrent = sketchImages.length + moodImages.length + wallsMaterial.length + sofasMaterial.length + ceilingMaterial.length + floorMaterial.length;
    const remaining = Math.max(0, MAX_UPLOADS - totalCurrent);
    if (remaining === 0) return;
    const limitedFiles = files.slice(0, remaining);
    const imageUrls = (await Promise.all(limitedFiles.map((file) => storageService.uploadFile(file, user?.id)))).filter(Boolean);

    switch (materialType) {
      case 'walls':
        setWallsMaterial([...wallsMaterial, ...imageUrls]);
        break;
      case 'sofas':
        setSofasMaterial([...sofasMaterial, ...imageUrls]);
        break;
      case 'ceiling':
        setCeilingMaterial([...ceilingMaterial, ...imageUrls]);
        break;
      case 'floor':
        setFloorMaterial([...floorMaterial, ...imageUrls]);
        break;
      default:
        break;
    }
  };

  const handleRemoveSketchImage = (idx) => {
    setSketchImages((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleRemoveMoodImage = (idx) => {
    setMoodImages((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleRemoveMaterial = (idx, materialType) => {
    switch (materialType) {
      case 'walls':
        setWallsMaterial((prev) => prev.filter((_, i) => i !== idx));
        break;
      case 'sofas':
        setSofasMaterial((prev) => prev.filter((_, i) => i !== idx));
        break;
      case 'ceiling':
        setCeilingMaterial((prev) => prev.filter((_, i) => i !== idx));
        break;
      case 'floor':
        setFloorMaterial((prev) => prev.filter((_, i) => i !== idx));
        break;
      default:
        break;
    }
  };

  // Resize image to correct aspect ratio since Gemini ignores dimension instructions
  const resizeImageToCorrectDimensions = async (imageUrl, targetDimensions) => {
    return new Promise((resolve, reject) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();

      img.crossOrigin = 'anonymous';
      img.onload = () => {
        // Set canvas to target dimensions
        canvas.width = targetDimensions.width;
        canvas.height = targetDimensions.height;

        // Calculate how to fit the image (maintaining aspect ratio but filling canvas)
        const imgAspect = img.width / img.height;
        const canvasAspect = canvas.width / canvas.height;

        let drawWidth, drawHeight, drawX, drawY;

        if (imgAspect > canvasAspect) {
          // Image is wider - fit by height
          drawHeight = canvas.height;
          drawWidth = drawHeight * imgAspect;
          drawX = (canvas.width - drawWidth) / 2;
          drawY = 0;
        } else {
          // Image is taller - fit by width
          drawWidth = canvas.width;
          drawHeight = drawWidth / imgAspect;
          drawX = 0;
          drawY = (canvas.height - drawHeight) / 2;
        }

        // Fill background with a neutral color
        ctx.fillStyle = '#1a1a1a';
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        // Draw image
        ctx.drawImage(img, drawX, drawY, drawWidth, drawHeight);

        // Upload to storage instead of using temporary blob URL
        canvas.toBlob(
          async (blob) => {
            try {
              const reader = new FileReader();
              reader.onloadend = async () => {
                const uploadedUrl = await storageService.uploadImage(reader.result, user?.id);
                resolve(uploadedUrl || URL.createObjectURL(blob));
              };
              reader.readAsDataURL(blob);
            } catch {
              resolve(URL.createObjectURL(blob));
            }
          },
          'image/png',
        );
      };

      img.onerror = () => reject(new Error('Failed to load image for resizing'));
      img.src = imageUrl;
    });
  };

  const handleDownload = async () => {
    if (!currentImage) return;
    setButtonLoading('download');
    await downloadImage(currentImage, `interior-render-${Date.now()}.png`);
    setButtonLoading(null);
  };

  const handleEditClick = () => {
    if (!currentImage) return;
    setButtonLoading('edit');
    // Edit logic here
    console.log('Edit clicked');
    setButtonLoading(null);
  };

  const handleDeleteClick = () => {
    if (!currentImage) return;
    setCurrentImage(null);
    setGenerationError(null);
  };

  const handleRegenerate = () => {
    if (!currentImage) return;
    setGenerationError(null);
    handleGenerate();
  };

  // Convert image URL to base64 data URI
  const urlToBase64 = async (url) => {
    return new Promise((resolve, reject) => {
      fetch(url)
        .then(res => res.blob())
        .then(blob => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        })
        .catch(reject);
    });
  };

  // Map aspect ratio to Gemini size format
  const mapAspectRatioToSize = (ratio) => {
    const sizeMap = {
      '1:1': '1024x1024',
      '4:3': '1024x768',
      '16:9': '1792x1024',
      '21:9': '1792x768',
      '5:4': '1024x819',
      '3:2': '1536x1024',
      '2:3': '1024x1536',
      '9:16': '1024x1792',
      '3:4': '768x1024',
      '4:5': '819x1024'
    };
    return sizeMap?.[ratio] || '1024x1024';
  };


  const handleGenerate = async () => {
    if (isGeneratingRef.current) return;
    isGeneratingRef.current = true;

    // 1. sketchImages - REQUIRED (block if empty)
    if (!sketchImages || sketchImages?.length === 0) {
      toast?.error('Sketch(es) are required. Please upload at least one sketch image.');
      isGeneratingRef.current = false;
      return;
    }

    // 2. moodImages - REQUIRED (block if empty)
    if (!moodImages || moodImages?.length === 0) {
      toast?.error('Mood image(s) are required. Please upload at least one mood image.');
      isGeneratingRef.current = false;
      return;
    }

    // Auth check
    const { data: { session }, error: sessionError } = await supabase?.auth?.getSession();
    if (!session || !session?.user?.id) {
      toast?.error('Please sign in to generate images.');
      isGeneratingRef.current = false;
      return;
    }
    const currentUser = session?.user;

    // Credit check (supports team members sharing owner's pool)
    const creditCheck = await userProfilesService?.checkCredits(currentUser?.id, getModelCreditCost(selectedModel));
    if (!creditCheck?.allowed) {
      toast?.error(creditCheck?.message || 'Insufficient credits.');
      isGeneratingRef.current = false;
      return;
    }

    // Check storage limit
    {
      const { allowed, current, max } = await checkSaveDesignLimit(currentUser?.id, userProfile?.subscription_tier);
      if (!allowed) {
        toast?.error(`Storage limit reached (${current}/${max}). Please upgrade your plan or delete existing files.`);
        isGeneratingRef.current = false;
        return;
      }
    }

    setIsGenerating(true);
    startTimer();
    setCurrentImage(null);
    setGenerationError(null);
    abortControllerRef.current = new AbortController();

    try {
      // Prepare material images object for service
      const newMaterialImages = {};
      if (wallsMaterial?.length > 0) newMaterialImages.wallsMaterial = wallsMaterial[0];
      if (floorMaterial?.length > 0) newMaterialImages.floorMaterial = floorMaterial[0];
      if (ceilingMaterial?.length > 0) newMaterialImages.ceilingMaterial = ceilingMaterial[0];
      if (sofasMaterial?.length > 0) newMaterialImages.sofasChairsMaterial = sofasMaterial[0];

      // Process user inputs for new service
      const newPromptText = prompt?.trim();
      const newRoomType = (selectedRoomType === '--' || selectedRoomType === 'Custom') ? '' : selectedRoomType;

      // Get dimensions based on selected size
      const dimensions = getImageDimensions();

      // System prompt specific to sketch-to-interior-render (handles everything including input formatting)
      const sketchSystemPrompt = `Role: You are a neutral translator between interior design platform user inputs and Gemini 3 Pro image generation model for sketch-to-render visualization.
Send all image references and text inputs uploaded by the user directly to Gemini API in one single call.

Intro: /sketch-to-interior-render tool is intended to only convert sketches the user uploads (exact spatial outline of the room which is the base of generated image) into hyper realistic 3D room combined with mood image user uploads (only mood is used to apply to the sketch from mood image). Using the sketch as a strict guideline for the spatial outline and camera angle, apply the color palette, material harmony, and atmosphere from the mood reference images. The sketch outline with applied mood is then rendered as a hyper-realistic 3D room.

Task: Preserve the sketch outline structure precisely. Apply mood and atmosphere from the mood images only. Apply provided material images to their exact corresponding surfaces. Do not invent unrequested elements. Render with hyper realism and photorealism quality. Maintain a high consistency to sketch outline and mood of mood images. Preserve the logic and spatial adequacy of generated rooms according to the normal physical human world, with reasonable room dimensions. The room composition must be logically and aesthetically appealing and coherent

CRITICAL DIMENSION REQUIREMENT: The output image MUST be generated in the exact dimensions specified: ${dimensions.width}x${dimensions.height} pixels. Do not use the aspect ratio from any uploaded images (sketches or mood images). The user has specifically selected ${selectedImageSize} format and this must be respected exactly.

High-quality keywords to maintain in every generation: render the sketch in the user-provided mood, hyper realism, photorealism, 3Ds Max quality with textures and realistic surfaces, shot on professional camera

Temperature: 1 (default Gemini).

You will receive these inputs from user, and you need to send them to Gemini API in one call, in the following importance hierarchy from one to seven:

1. sketchImages (REQUIRED) — hand-drawn interior sketch by the user. This defines the exact composition, perspective, spatial layout, furniture placement, and room structure. Preserve each contour of room outline and furniture visible in the sketch precisely.

2. moodImages (REQUIRED) — atmosphere and style reference images. Extract ONLY the color palette, material colors and textures, and overall visual mood visible in these images. Apply it to the sketch structure only.

3. wallsMaterial / floorMaterial / ceilingMaterial / sofasChairsMaterial — if provided, apply each material to its exact corresponding surface per sketch outline logic. Do not substitute or ignore.

4. promptText — include the user's text literally, word for word${newPromptText ? `
   USER PROMPT TEXT: ${newPromptText}` : ''}

5. roomType — include as spatial context only, if not '--'${newRoomType ? `
   ROOM TYPE: ${newRoomType}` : ''}

6. imageSize — CRITICAL: Generate image in EXACT dimensions specified by user: ${dimensions.width}x${dimensions.height} pixels (${selectedImageSize} aspect ratio). DO NOT use aspect ratio from mood images or sketch images. The output image MUST be exactly ${dimensions.width} pixels wide by ${dimensions.height} pixels tall. Ignore any aspect ratios from uploaded images.
   REQUIRED OUTPUT DIMENSIONS: ${dimensions.width}x${dimensions.height} pixels (${selectedImageSize})

8. modelSelection — use only to determine which image generation API to call. Do not pass the model name as text in the prompt.

STRICT RULES:
- Do not invent materials, furniture, lighting or atmosphere not present in the inputs.
- Do not use any style labels unless the user wrote them in promptText. Strictly follow user's reference images and prompt, and the following task you're trained on related to sketch hyper realistic rendering
- Sketch structure is absolute — never alter composition or perspective.
- Mood images define atmosphere only — never override sketch structure.
- If any input is missing or '--', skip it silently.
- No explanations, no formatting, no headings in output.`;

      const baseInstruction = "Generate exactly what the prompt describes. Do not add style, materials, or aesthetic elements not mentioned in the prompt. Render with hyper realism and photorealism quality.";
      const fullPrompt = `${baseInstruction}\n\n${sketchSystemPrompt}`;

      // Route to appropriate service based on selected model
      let generatedImageUrl;

      if (isFluxModel(selectedModel)) {
        generatedImageUrl = await generateWithFlux({
          modelName: selectedModel,
          directPromptText: fullPrompt,
          userPromptText: newPromptText,
          moodboardImages: moodImages,
          referenceImages: sketchImages,
          roomType: newRoomType,
          imageSize: selectedImageSize,
          signal: abortControllerRef.current.signal,
        });
      } else {
        generatedImageUrl = await generateSketchToInterior({
          sketchImages: sketchImages,
          moodImages: moodImages,
          userPromptText: newPromptText,
          roomType: newRoomType,
          materialImages: newMaterialImages,
          imageSize: selectedImageSize,
          modelName: selectedModel,
          dimensions: dimensions,
          toolSystemPrompt: fullPrompt,
          signal: abortControllerRef.current.signal,
        });
      }

      // Resize image to correct dimensions since Gemini ignores dimension instructions
      const resizedImageUrl = await resizeImageToCorrectDimensions(generatedImageUrl, dimensions);
      setCurrentImage(resizedImageUrl);
      setLibraryAssets((prev) => [...prev, resizedImageUrl]);
      setSessionLibraryImages((prev) => [...prev, resizedImageUrl]);
      clearTimer();
      setIsGenerating(false);
      setGenerationError('');
      toast?.success('Image generated successfully!');
      showSaveModal(resizedImageUrl, location.pathname);

      // Save to database
      await saveGenerationToDatabase(resizedImageUrl, prompt?.trim() || '', currentUser);
      // Deduct credits
      await userProfilesService?.updateCredits(currentUser?.id, getModelCreditCost(selectedModel));

    } catch (error) {
      if (error?.name === 'AbortError' || error?.message?.includes('cancelled')) {
        setCancelled();
      } else {
        console.error('Error generating image:', error);
        setFailed();
        setGenerationError(error?.message || 'Failed to generate image');
      }
    } finally {
      setIsGenerating(false);
      abortControllerRef.current = null;
      isGeneratingRef.current = false;
    }
  };

  const saveGenerationToDatabase = async (imageUrl, prompt, currentUser = null) => {
    const activeUser = currentUser || user;
    if (!activeUser) return;

    try {
      // Upload image to Supabase Storage (convert base64 → public URL)
      const storageUrl = await storageService.uploadImage(imageUrl, activeUser?.id);
      imageUrl = storageUrl;

      let projectId = currentProjectId;
      if (!projectId) {
        const projectName = `${selectedRoomType || 'Room'} - ${new Date()?.toLocaleDateString()}`;
        const { data: project, error: projectError } = await projectsService?.createProject(activeUser?.id, {
          name: projectName,
          description: prompt,
          thumbnail: imageUrl,
          toolType: 'sketch-to-render',
          toolName: 'Sketch to Interior Render',
          status: 'completed',
          tags: [selectedRoomType, selectedModel]?.filter(Boolean),
          variations: 1,
        });
        if (projectError) { console.error('Error creating project:', projectError); return; }
        projectId = project?.id;
        setCurrentProjectId(projectId);
      } else {
        await projectsService?.incrementVariations(projectId);
        await projectsService?.updateProject(projectId, { thumbnail: imageUrl });
      }

      const { data: design, error: designError } = await savedDesignsService?.createDesign(activeUser?.id, projectId, {
        imageUrl,
        prompt,
        isFavorite: false,
      });
      if (designError) { console.error('Error saving design:', designError); return; }

      const dimensions = getImageDimensions();
      await generationMetadataService?.createMetadata(activeUser?.id, design?.id, {
        modelUsed: selectedModel,
        imageSize: `${dimensions.width}x${dimensions.height}`,
        roomType: selectedRoomType || 'Room',
        style: 'Generated',
        resolution: `${dimensions.width}x${dimensions.height}`,
        lighting: 'Auto',
        quality: 'High',
        usedMedia: [...sketchImages, ...moodImages, imageUrl],
        additionalParams: {
          prompt,
        },
      });
    } catch (error) {
      console.error('Error saving to database:', error);
    }
  };

  const handleCancelGeneration = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      setCancelled();
      setIsGenerating(false);
    }
  };

  // Restore project data when opened from /project-history
  useEffect(() => {
    const projectId = location.state?.projectId;
    if (!projectId) return;
    const loadProjectData = async () => {
      setCurrentProjectId(projectId);
      const designs = await savedDesignsService.getDesigns(projectId);
      if (designs && designs.length > 0) {
        const imageUrls = designs.map((d) => d.imageUrl).filter(Boolean);
        if (imageUrls.length > 0) setSessionLibraryImages(imageUrls);
        setCurrentImage(designs[0].imageUrl);
        if (designs[0]?.prompt) setPrompt(designs[0].prompt);
        // Restore generation settings from metadata
        const metadata = await generationMetadataService.getMetadata(designs[0].id);
        if (metadata) {
          if (metadata.modelUsed) setSelectedModel(metadata.modelUsed);
          if (metadata.imageSize) setSelectedImageSize(metadata.imageSize);
          if (metadata.roomType && metadata.roomType !== 'Room') setSelectedRoomType(metadata.roomType);
          if (metadata?.usedMedia?.length > 0) setLibraryAssets(metadata.usedMedia);
          const extra = metadata.additionalParams || {};
          if (extra.prompt && !designs[0]?.prompt) setPrompt(extra.prompt);
        }
      }
    };
    loadProjectData();
  }, []);

  // Auto-resize textarea
  useEffect(() => {
    if (promptRef?.current) {
      promptRef.current.style.height = 'auto';
      promptRef.current.style.height = promptRef?.current?.scrollHeight + 'px';
    }
  }, [prompt]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      const inDesktopAR = aspectRatioRef.current && aspectRatioRef.current.contains(event.target);
      const inMobileAR = aspectRatioMobileRef.current && aspectRatioMobileRef.current.contains(event.target);
      if (!inDesktopAR && !inMobileAR) {
        setIsAspectRatioOpen(false);
      }
      const inDesktopRes = resolutionRef.current && resolutionRef.current.contains(event.target);
      const inMobileRes = resolutionMobileRef.current && resolutionMobileRef.current.contains(event.target);
      if (!inDesktopRes && !inMobileRes) {
        setIsResolutionOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const getResolutionDimensions = (resKey) => {
    const ratioMap = resolutionMap[selectedImageSize];
    if (ratioMap && ratioMap[resKey]) {
      return ratioMap[resKey];
    }
    return resolutionMap['Auto'][resKey] || { width: 1024, height: 1024 };
  };

  const handlePromptChange = (e) => {
    const value = e?.target?.value;
    if (value?.length <= MAX_PROMPT_LENGTH) {
      setPrompt(value);
    }
  };

  return (
    <div className="min-h-screen bg-black overflow-x-hidden">
      <GenerationStatusNotification status={generationStatus} onDismiss={dismissNotification} />
      <Toaster position="top-center" />
      <Header />
      <main className="pt-20 lg:pt-24 px-4 pb-[20vh]">
        <div className="max-w-[1400px] mx-auto">
          {/* Back to Dashboard and Title */}
          <div className="mb-6">
            <button
              onClick={() => navigate('/main-dashboard')}
              className="flex items-center space-x-2 text-white/70 hover:text-white transition-colors mb-4">

              <Icon name="ArrowLeft" size={20} />
              <span className="text-sm font-medium">Back to Dashboard</span>
            </button>

            <div className="flex items-center justify-between">
              <h1 className="text-3xl font-bold text-white">
                Sketch to Interior Render
              </h1>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => openGuide()}
                  className="flex items-center gap-2 px-4 py-2 rounded-xl text-white text-sm font-medium hover:scale-[1.02] transition-all flex-shrink-0"
                  style={{ background: 'rgba(255, 255, 255, 0.22)', backdropFilter: 'blur(16px)', WebkitBackdropFilter: 'blur(16px)', border: '1px solid rgba(255, 255, 255, 0.4)', boxShadow: '0 4px 16px rgba(0, 0, 0, 0.12), inset 0 1px 0 rgba(255, 255, 255, 0.25)' }}
                >
                  <Icon name="HelpCircle" size={18} />
                  <span className="hidden md:inline">Virtual Tool Guide</span>
                </button>
                <button
                  onClick={() => navigate('/project-history')}
                  className="flex items-center gap-2 px-4 py-2 glassmorphic-button rounded-xl text-white text-sm hover:scale-105 transition-transform flex-shrink-0">
                  <Icon name="History" size={18} />
                  <span className="hidden md:inline text-sm font-medium">View Projects</span>
                </button>
              </div>
            </div>
          </div>

          {/* Upload Counter - Desktop */}
          <div className="hidden md:block text-left mb-2">
            <span className={`text-sm font-medium ${totalUploads >= MAX_UPLOADS ? "text-red-400" : "text-white/70"}`}>
              {totalUploads} / {MAX_UPLOADS} images uploaded
            </span>
          </div>

          {/* Desktop/Tablet/Laptop Layout (hidden on mobile) */}
          <div className="hidden md:flex gap-4">
            {/* Left Panel - First Third: Sketch/Mood Uploads + Sketch Optimization */}
            <div className="w-1/3 flex-shrink-0 space-y-4">
              {/* Sketch(es) Upload */}
              <div ref={sketchUploadDesktopRef} className="rounded-xl" style={{ borderRadius: '1.5rem', position: showGuide && guideStep === 1 ? 'relative' : undefined, zIndex: showGuide && guideStep === 1 ? 201 : undefined, boxShadow: showGuide && guideStep === 1 && guideVisible ? '0 0 32px rgba(180, 120, 60, 0.5), 0 0 80px rgba(180, 120, 60, 0.35), 0 0 150px rgba(180, 120, 60, 0.2)' : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4, 0, 0.2, 1)' }}>
              <div className="glassmorphic-card rounded-3xl p-6 border border-transparent transition-all duration-200"
                onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'copy'; e.currentTarget.classList.add('border-orange-500/50'); }}
                onDragLeave={(e) => { e.currentTarget.classList.remove('border-orange-500/50'); }}
                onDrop={(e) => {
                  e.preventDefault();
                  e.currentTarget.classList.remove('border-orange-500/50');
                  const imageUrl = e.dataTransfer.getData('imageUrl') || e.dataTransfer.getData('text/plain');
                  if (imageUrl) {
                    if (totalUploads < MAX_UPLOADS) {
                      setSketchImages((prev) => prev.includes(imageUrl) ? prev : [...prev, imageUrl]);
                    }
                    return;
                  }
                }}>
                <h3 className="text-[12.6px] font-medium text-white mb-4">Sketch(es)<span className="text-red-500">*</span></h3>
                <label className={`block ${totalUploads >= MAX_UPLOADS ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}>
                  <input
                    type="file"
                    multiple
                    accept="image/png,image/jpeg"
                    onChange={handleSketchUpload}
                    disabled={totalUploads >= MAX_UPLOADS}
                    className="hidden" />

                  <div className={`glassmorphic-upload-zone rounded-2xl p-8 text-center border-2 border-dashed transition-all duration-300 ${totalUploads >= MAX_UPLOADS ? 'border-white/10' : 'border-white/20 hover:border-orange-500/40'}`}>
                    <Icon name="Image" size={40} className="text-orange-500 mx-auto mb-3" />
                    <p className="text-white/70 text-sm">Upload image PNG or JPG</p>
                  </div>
                </label>
                {sketchImages?.length > 0 &&
                <div className="mt-3 flex flex-wrap gap-1.5">
                    {sketchImages?.map((img, idx) =>
                  <UploadedImageThumb key={idx} src={img} alt={`Sketch ${idx + 1}`} onRemove={() => handleRemoveSketchImage(idx)} imgClass="max-w-[80px] max-h-[60px] object-contain rounded-lg border border-white/10" height="h-auto" previewWidth="w-[200px]" />
                  )}
                  </div>
                }
              </div>
              </div>

              {/* Mood image(s) Upload */}
              <div ref={moodUploadDesktopRef} className="rounded-xl" style={{ borderRadius: '1.5rem', position: showGuide && guideStep === 2 ? 'relative' : undefined, zIndex: showGuide && guideStep === 2 ? 201 : undefined, boxShadow: showGuide && guideStep === 2 && guideVisible ? '0 0 32px rgba(180, 120, 60, 0.5), 0 0 80px rgba(180, 120, 60, 0.35), 0 0 150px rgba(180, 120, 60, 0.2)' : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4, 0, 0.2, 1)' }}>
              <div className="glassmorphic-card rounded-3xl p-6 border border-transparent transition-all duration-200"
                onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'copy'; e.currentTarget.classList.add('border-orange-500/50'); }}
                onDragLeave={(e) => { e.currentTarget.classList.remove('border-orange-500/50'); }}
                onDrop={(e) => {
                  e.preventDefault();
                  e.currentTarget.classList.remove('border-orange-500/50');
                  const imageUrl = e.dataTransfer.getData('imageUrl') || e.dataTransfer.getData('text/plain');
                  if (imageUrl) {
                    if (totalUploads < MAX_UPLOADS) {
                      setMoodImages((prev) => prev.includes(imageUrl) ? prev : [...prev, imageUrl]);
                    }
                    return;
                  }
                }}>
                <h3 className="text-[12.6px] font-medium text-white mb-4">Mood image(s)<span className="text-red-500">*</span></h3>
                <label className={`block ${totalUploads >= MAX_UPLOADS ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}>
                  <input
                    type="file"
                    multiple
                    accept="image/png,image/jpeg"
                    onChange={handleMoodUpload}
                    disabled={totalUploads >= MAX_UPLOADS}
                    className="hidden" />

                  <div className={`glassmorphic-upload-zone rounded-2xl p-8 text-center border-2 border-dashed transition-all duration-300 ${totalUploads >= MAX_UPLOADS ? 'border-white/10' : 'border-white/20 hover:border-orange-500/40'}`}>
                    <Icon name="Image" size={40} className="text-orange-500 mx-auto mb-3" />
                    <p className="text-white/70 text-sm">Upload image PNG or JPG</p>
                  </div>
                </label>
                {moodImages?.length > 0 &&
                <div className="mt-3 flex flex-wrap gap-1.5">
                    {moodImages?.map((img, idx) =>
                  <UploadedImageThumb key={idx} src={img} alt={`Mood ${idx + 1}`} onRemove={() => handleRemoveMoodImage(idx)} imgClass="max-w-[80px] max-h-[60px] object-contain rounded-lg border border-white/10" height="h-auto" previewWidth="w-[200px]" />
                  )}
                  </div>
                }
              </div>
              </div>

              {/* Sketch Optimization Section */}
              <div className="glassmorphic-card rounded-3xl p-6 space-y-4">
                <h3 className="text-[12.6px] font-semibold text-white mb-4">Sketch optimization</h3>

                {/* Room Type Dropdown */}
                <div ref={roomTypeDesktopRef} className="rounded-xl" style={{ borderRadius: '1rem', position: showGuide && guideStep === 3 ? 'relative' : undefined, zIndex: showGuide && guideStep === 3 ? 201 : undefined, boxShadow: showGuide && guideStep === 3 && guideVisible ? '0 0 32px rgba(180, 120, 60, 0.5), 0 0 80px rgba(180, 120, 60, 0.35), 0 0 150px rgba(180, 120, 60, 0.2)' : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4, 0, 0.2, 1)' }}>
                <RoomTypeDropdown
                  value={selectedRoomType}
                  onChange={(val) => {
                    setSelectedRoomType(val);
                    if (val === 'Custom') {
                      setTimeout(() => {
                        if (promptRef?.current) {
                          promptRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
                          promptRef.current.focus();
                        }
                      }, 80);
                    }
                  }}
                  showCustomOption
                />
                </div>

                {/* Walls Material Upload */}
                <div ref={materialsDesktopRef} className="rounded-xl space-y-4" style={{ borderRadius: '1rem', position: showGuide && guideStep === 4 ? 'relative' : undefined, zIndex: showGuide && guideStep === 4 ? 201 : undefined, boxShadow: showGuide && guideStep === 4 && guideVisible ? '0 0 32px rgba(180, 120, 60, 0.5), 0 0 80px rgba(180, 120, 60, 0.35), 0 0 150px rgba(180, 120, 60, 0.2)' : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4, 0, 0.2, 1)' }}>
                <div className="border border-transparent transition-all duration-200 rounded-2xl"
                  onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'copy'; e.currentTarget.classList.add('border-orange-500/50'); }}
                  onDragLeave={(e) => { e.currentTarget.classList.remove('border-orange-500/50'); }}
                  onDrop={(e) => {
                    e.preventDefault();
                    e.currentTarget.classList.remove('border-orange-500/50');
                    const imageUrl = e.dataTransfer.getData('imageUrl') || e.dataTransfer.getData('text/plain');
                    if (imageUrl) {
                      if (totalUploads < MAX_UPLOADS) {
                        setWallsMaterial((prev) => prev.includes(imageUrl) ? prev : [...prev, imageUrl]);
                      }
                      return;
                    }
                  }}>
                  <label className="text-white/70 text-[12.6px] mb-2 block">Walls</label>
                  <label className={`block ${totalUploads >= MAX_UPLOADS ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}>
                    <input
                      type="file"
                      multiple
                      accept="image/png,image/jpeg"
                      onChange={(e) => handleMaterialUpload(e, 'walls')}
                      disabled={totalUploads >= MAX_UPLOADS}
                      className="hidden" />

                    <div className={`glassmorphic-upload-zone rounded-2xl p-4 text-center border-2 border-dashed transition-all duration-300 ${totalUploads >= MAX_UPLOADS ? 'border-white/10' : 'border-white/20 hover:border-orange-500/40'}`}>
                      <Icon name="Upload" size={24} className="text-orange-500 mx-auto mb-1" />
                      <p className="text-white/70 text-xs">Upload material image</p>
                    </div>
                  </label>
                  {wallsMaterial?.length > 0 &&
                  <div className="mt-2 flex flex-wrap gap-1.5">
                      {wallsMaterial?.map((img, idx) =>
                    <UploadedImageThumb key={idx} src={img} alt={`Walls material ${idx + 1}`} onRemove={() => handleRemoveMaterial(idx, 'walls')} imgClass="max-w-[80px] max-h-[60px] object-contain rounded-lg border border-white/10" height="h-auto" previewWidth="w-[200px]" />
                    )}
                    </div>
                  }
                </div>

                {/* Sofas/chairs Material Upload */}
                <div className="border border-transparent transition-all duration-200 rounded-2xl"
                  onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'copy'; e.currentTarget.classList.add('border-orange-500/50'); }}
                  onDragLeave={(e) => { e.currentTarget.classList.remove('border-orange-500/50'); }}
                  onDrop={(e) => {
                    e.preventDefault();
                    e.currentTarget.classList.remove('border-orange-500/50');
                    const imageUrl = e.dataTransfer.getData('imageUrl') || e.dataTransfer.getData('text/plain');
                    if (imageUrl) {
                      if (totalUploads < MAX_UPLOADS) {
                        setSofasMaterial((prev) => prev.includes(imageUrl) ? prev : [...prev, imageUrl]);
                      }
                      return;
                    }
                  }}>
                  <label className="text-white/70 text-[12.6px] mb-2 block">Sofas / chairs</label>
                  <label className={`block ${totalUploads >= MAX_UPLOADS ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}>
                    <input
                      type="file"
                      multiple
                      accept="image/png,image/jpeg"
                      onChange={(e) => handleMaterialUpload(e, 'sofas')}
                      disabled={totalUploads >= MAX_UPLOADS}
                      className="hidden" />

                    <div className={`glassmorphic-upload-zone rounded-2xl p-4 text-center border-2 border-dashed transition-all duration-300 ${totalUploads >= MAX_UPLOADS ? 'border-white/10' : 'border-white/20 hover:border-orange-500/40'}`}>
                      <Icon name="Upload" size={24} className="text-orange-500 mx-auto mb-1" />
                      <p className="text-white/70 text-xs">Upload material image</p>
                    </div>
                  </label>
                  {sofasMaterial?.length > 0 &&
                  <div className="mt-2 flex flex-wrap gap-1.5">
                      {sofasMaterial?.map((img, idx) =>
                    <UploadedImageThumb key={idx} src={img} alt={`Sofas material ${idx + 1}`} onRemove={() => handleRemoveMaterial(idx, 'sofas')} imgClass="max-w-[80px] max-h-[60px] object-contain rounded-lg border border-white/10" height="h-auto" previewWidth="w-[200px]" />
                    )}
                    </div>
                  }
                </div>

                {/* Ceiling Material Upload */}
                <div className="border border-transparent transition-all duration-200 rounded-2xl"
                  onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'copy'; e.currentTarget.classList.add('border-orange-500/50'); }}
                  onDragLeave={(e) => { e.currentTarget.classList.remove('border-orange-500/50'); }}
                  onDrop={(e) => {
                    e.preventDefault();
                    e.currentTarget.classList.remove('border-orange-500/50');
                    const imageUrl = e.dataTransfer.getData('imageUrl') || e.dataTransfer.getData('text/plain');
                    if (imageUrl) {
                      if (totalUploads < MAX_UPLOADS) {
                        setCeilingMaterial((prev) => prev.includes(imageUrl) ? prev : [...prev, imageUrl]);
                      }
                      return;
                    }
                  }}>
                  <label className="text-white/70 text-[12.6px] mb-2 block">Ceiling</label>
                  <label className={`block ${totalUploads >= MAX_UPLOADS ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}>
                    <input
                      type="file"
                      multiple
                      accept="image/png,image/jpeg"
                      onChange={(e) => handleMaterialUpload(e, 'ceiling')}
                      disabled={totalUploads >= MAX_UPLOADS}
                      className="hidden" />

                    <div className={`glassmorphic-upload-zone rounded-2xl p-4 text-center border-2 border-dashed transition-all duration-300 ${totalUploads >= MAX_UPLOADS ? 'border-white/10' : 'border-white/20 hover:border-orange-500/40'}`}>
                      <Icon name="Upload" size={24} className="text-orange-500 mx-auto mb-1" />
                      <p className="text-white/70 text-xs">Upload material image</p>
                    </div>
                  </label>
                  {ceilingMaterial?.length > 0 &&
                  <div className="mt-2 flex flex-wrap gap-1.5">
                      {ceilingMaterial?.map((img, idx) =>
                    <UploadedImageThumb key={idx} src={img} alt={`Ceiling material ${idx + 1}`} onRemove={() => handleRemoveMaterial(idx, 'ceiling')} imgClass="max-w-[80px] max-h-[60px] object-contain rounded-lg border border-white/10" height="h-auto" previewWidth="w-[200px]" />
                    )}
                    </div>
                  }
                </div>

                {/* Floor Material Upload */}
                <div className="border border-transparent transition-all duration-200 rounded-2xl"
                  onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'copy'; e.currentTarget.classList.add('border-orange-500/50'); }}
                  onDragLeave={(e) => { e.currentTarget.classList.remove('border-orange-500/50'); }}
                  onDrop={(e) => {
                    e.preventDefault();
                    e.currentTarget.classList.remove('border-orange-500/50');
                    const imageUrl = e.dataTransfer.getData('imageUrl') || e.dataTransfer.getData('text/plain');
                    if (imageUrl) {
                      if (totalUploads < MAX_UPLOADS) {
                        setFloorMaterial((prev) => prev.includes(imageUrl) ? prev : [...prev, imageUrl]);
                      }
                      return;
                    }
                  }}>
                  <label className="text-white/70 text-[12.6px] mb-2 block">Floor</label>
                  <label className={`block ${totalUploads >= MAX_UPLOADS ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}>
                    <input
                      type="file"
                      multiple
                      accept="image/png,image/jpeg"
                      onChange={(e) => handleMaterialUpload(e, 'floor')}
                      disabled={totalUploads >= MAX_UPLOADS}
                      className="hidden" />

                    <div className={`glassmorphic-upload-zone rounded-2xl p-4 text-center border-2 border-dashed transition-all duration-300 ${totalUploads >= MAX_UPLOADS ? 'border-white/10' : 'border-white/20 hover:border-orange-500/40'}`}>
                      <Icon name="Upload" size={24} className="text-orange-500 mx-auto mb-1" />
                      <p className="text-white/70 text-xs">Upload material image</p>
                    </div>
                  </label>
                  {floorMaterial?.length > 0 &&
                  <div className="mt-2 flex flex-wrap gap-1.5">
                      {floorMaterial?.map((img, idx) =>
                    <UploadedImageThumb key={idx} src={img} alt={`Floor material ${idx + 1}`} onRemove={() => handleRemoveMaterial(idx, 'floor')} imgClass="max-w-[80px] max-h-[60px] object-contain rounded-lg border border-white/10" height="h-auto" previewWidth="w-[200px]" />
                    )}
                    </div>
                  }
                </div>
                </div>

              </div>

              {/* Asset Library Panel */}
              <div ref={assetLibraryDesktopRef} className="rounded-2xl" style={{ borderRadius: '1.5rem', position: showGuide && guideStep === 5 ? 'relative' : undefined, zIndex: showGuide && guideStep === 5 ? 201 : undefined, boxShadow: showGuide && guideStep === 5 && guideVisible ? '0 0 32px rgba(180, 120, 60, 0.5), 0 0 80px rgba(180, 120, 60, 0.35), 0 0 150px rgba(180, 120, 60, 0.2)' : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4, 0, 0.2, 1)' }}>
              <AssetLibraryPanel
                libraryAssets={libraryAssets}
                setLibraryAssets={setLibraryAssets}
                generatedImages={sessionLibraryImages}
                setGeneratedImages={setSessionLibraryImages}
                currentProjectId={currentProjectId}
                onImageDeleted={(remaining) => {
                  if (!isGenerating) setCurrentImage(remaining[0] || null);
                }}
              />
              </div>
            </div>

            {/* Right Panel - Two Thirds: Result and Model Selection */}
            <div className="flex-1 space-y-4">
              {/* Main Image Display - Result */}
              <div ref={resultDesktopRef} className="rounded-2xl" style={{ borderRadius: '1.5rem', position: showGuide && guideStep === 11 ? 'relative' : undefined, zIndex: showGuide && guideStep === 11 ? 201 : undefined, boxShadow: showGuide && guideStep === 11 && guideVisible ? '0 0 32px rgba(180, 120, 60, 0.5), 0 0 80px rgba(180, 120, 60, 0.35), 0 0 150px rgba(180, 120, 60, 0.2)' : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4, 0, 0.2, 1)' }}>
              <div className="glassmorphic-card rounded-3xl p-4 relative">
                <div className="flex justify-between items-center mb-3 px-2">
                  <span className="text-white/70 text-[12.6px]"></span>
                  <span className="text-white/70 text-[12.6px]">Result</span>
                </div>
                <div className="relative rounded-2xl overflow-hidden bg-black/40 h-[500px]">
                  {isGenerating ? (
                    <div className="w-full h-full flex items-center justify-center">
                      <div className="text-center">
                        <Icon name="Loader" size={48} className="text-orange-500 mx-auto mb-3 animate-spin" />
                        <p className="text-white text-sm font-medium">Generating your interior render...</p>
                        <p className="text-white/60 text-xs mt-1">This may take a moment</p>
                      </div>
                    </div>
                  ) : currentImage ? (
                    <img
                      src={currentImage}
                      alt="Generated interior render"
                      className="w-full h-full object-contain" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <div className="text-center">
                        <p className="text-white/50 text-sm">No image generated yet</p>
                        <p className="text-white/30 text-xs mt-1">Upload images and click Generate</p>
                      </div>
                    </div>
                  )}

                  {/* Error message overlay */}
                  {generationError && !isGenerating && (
                    <div className="absolute inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center">
                      <div className="text-center max-w-md px-4">
                        <Icon name="AlertCircle" size={48} className="text-red-500 mx-auto mb-3" />
                        <p className="text-white text-sm font-medium mb-2">Generation Failed</p>
                        <p className="text-white/70 text-xs">{generationError}</p>
                      </div>
                    </div>
                  )}

                  {/* Icon buttons overlay - visible in bottom right corner */}
                  <div className="absolute bottom-4 right-4 flex items-center space-x-2">
                    <button
                      onClick={handleDownload}
                      disabled={!currentImage || buttonLoading !== null || isGenerating}
                      className="p-2.5 rounded-lg bg-black/70 hover:bg-black/80 border border-white/30 shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Download">
                      {buttonLoading === 'download' ? (
                        <Icon name="Loader" size={18} className="text-white animate-spin" />
                      ) : (
                        <Icon name="Download" size={18} className="text-white" />
                      )}
                    </button>
                    <button
                      onClick={handleDeleteClick}
                      disabled={!currentImage || buttonLoading !== null || isGenerating}
                      className="p-2.5 rounded-lg bg-black/70 hover:bg-black/80 border border-white/30 shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Delete">
                      <Icon name="Trash2" size={18} className="text-white" />
                    </button>
                    <button
                      onClick={handleRegenerate}
                      disabled={!currentImage || buttonLoading !== null || isGenerating}
                      className="p-2.5 rounded-lg bg-black/70 hover:bg-black/80 border border-white/30 shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Regenerate">
                      {isGenerating ? (
                        <Icon name="Loader" size={18} className="text-white animate-spin" />
                      ) : (
                        <Icon name="RefreshCw" size={18} className="text-white" />
                      )}
                    </button>
                  </div>
                </div>
              </div>
              </div>

              {/* Model Selection and Controls - 2x2 Grid Layout */}
              <div className="glassmorphic-card rounded-3xl p-6">
                {/* First Level: Model Selection & Aspect Ratio */}
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div ref={modelSelectionDesktopRef} className="rounded-xl" style={{ borderRadius: '1rem', position: showGuide && guideStep === 6 ? 'relative' : undefined, zIndex: showGuide && guideStep === 6 ? 201 : undefined, boxShadow: showGuide && guideStep === 6 && guideVisible ? '0 0 32px rgba(180, 120, 60, 0.5), 0 0 80px rgba(180, 120, 60, 0.35), 0 0 150px rgba(180, 120, 60, 0.2)' : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4, 0, 0.2, 1)' }}>
                    <ModelSelectionDropdown
                      value={selectedModel}
                      onChange={setSelectedModel}
                    />
                  </div>

                  <div ref={aspectRatioGlowDesktopRef} className="rounded-xl" style={{ borderRadius: '1rem', position: showGuide && guideStep === 7 ? 'relative' : undefined, zIndex: showGuide && guideStep === 7 ? 201 : undefined, boxShadow: showGuide && guideStep === 7 && guideVisible ? '0 0 32px rgba(180, 120, 60, 0.5), 0 0 80px rgba(180, 120, 60, 0.35), 0 0 150px rgba(180, 120, 60, 0.2)' : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4, 0, 0.2, 1)' }}>
                  <div className="relative" ref={aspectRatioRef}>
                    <label className="text-white/70 text-xs mb-2 block">Aspect Ratio</label>
                    <div className="relative">
                      <button
                        type="button"
                        onClick={() => setIsAspectRatioOpen(!isAspectRatioOpen)}
                        className="w-full glassmorphic-select rounded-2xl px-4 py-3 bg-white/5 border border-white/10 flex items-center justify-between hover:border-orange-500/30 transition-all"
                      >
                        <div className="flex items-center gap-3">
                          <AspectRatioIcon ratio={selectedImageSize} size={18} className="text-orange-500" />
                          <span className="text-white text-sm">{selectedImageSize}</span>
                        </div>
                        <Icon name={isAspectRatioOpen ? 'ChevronUp' : 'ChevronDown'} size={16} className="text-white/50" />
                      </button>
                      {isAspectRatioOpen && (
                        <div
                          className="absolute z-50 w-full bottom-full mb-2 rounded-2xl border border-white/10 shadow-2xl overflow-hidden"
                          style={{ backgroundColor: 'rgba(0, 0, 0, 0.95)', backdropFilter: 'blur(8px)' }}
                          onMouseDown={(e) => e.preventDefault()}
                        >
                          {aspectRatios?.map((ratio) => (
                            <button
                              key={ratio?.value}
                              type="button"
                              onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); setSelectedImageSize(ratio?.value); setIsAspectRatioOpen(false); }}
                              className={`w-full flex items-center justify-between px-4 py-3 transition-all border-b border-white/5 last:border-b-0 ${
                                selectedImageSize === ratio?.value
                                  ? 'bg-orange-500/20 hover:bg-orange-500/30'
                                  : 'hover:bg-white/5'
                              }`}
                            >
                              <div className="flex items-center gap-3">
                                <AspectRatioIcon
                                  ratio={ratio?.value}
                                  size={18}
                                  className={selectedImageSize === ratio?.value ? 'text-orange-500' : 'text-white/50'}
                                />
                                <span className={`text-sm ${selectedImageSize === ratio?.value ? 'text-white font-medium' : 'text-white/70'}`}>
                                  {ratio?.label}
                                </span>
                              </div>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                  </div>
                </div>

                {/* Resolution Selection */}
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div ref={resolutionGlowDesktopRef} className="rounded-xl" style={{ borderRadius: '1rem', position: showGuide && guideStep === 8 ? 'relative' : undefined, zIndex: showGuide && guideStep === 8 ? 201 : undefined, boxShadow: showGuide && guideStep === 8 && guideVisible ? '0 0 32px rgba(180, 120, 60, 0.5), 0 0 80px rgba(180, 120, 60, 0.35), 0 0 150px rgba(180, 120, 60, 0.2)' : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4, 0, 0.2, 1)' }}>
                  {!isFluxModel(selectedModel) && (
                  <div className="relative" ref={resolutionRef}>
                    <label className="text-white/70 text-xs mb-2 block">Resolution</label>
                    <div className="relative">
                      <button
                        type="button"
                        onClick={() => setIsResolutionOpen(!isResolutionOpen)}
                        className={`w-full glassmorphic-select rounded-2xl px-4 py-3 bg-white/5 border border-white/10 flex items-center gap-3 justify-between hover:border-orange-500/30 transition-all`}
                      >
                        <span className="text-white text-sm font-medium">{selectedResolution}</span>
                        <div className="flex items-center gap-2">
                          <span className="text-white/50 text-xs">
                              {getResolutionDimensions(selectedResolution).width}×{getResolutionDimensions(selectedResolution).height}
                            </span>
                          <Icon name={isResolutionOpen ? 'ChevronUp' : 'ChevronDown'} size={16} className="text-white/50" />
                        </div>
                      </button>
                      {isResolutionOpen && (
                        <div
                          className="absolute z-50 w-full bottom-full mb-2 rounded-2xl border border-white/10 shadow-2xl overflow-hidden"
                          style={{ backgroundColor: 'rgba(0, 0, 0, 0.95)', backdropFilter: 'blur(8px)' }}
                          onMouseDown={(e) => e.preventDefault()}
                        >
                          {RESOLUTION_OPTIONS.map((resKey) => {
                            const dims = getResolutionDimensions(resKey);
                            return (
                              <button
                                key={resKey}
                                type="button"
                                onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); setSelectedResolution(resKey); setIsResolutionOpen(false); }}
                                className={`w-full flex items-center justify-between px-4 py-3 transition-all border-b border-white/5 last:border-b-0 ${
                                  selectedResolution === resKey
                                    ? 'bg-orange-500/20 hover:bg-orange-500/30'
                                    : 'hover:bg-white/5'
                                }`}
                              >
                                <span className={`text-sm ${selectedResolution === resKey ? 'text-white font-medium' : 'text-white/70'}`}>
                                  {resKey}
                                </span>
                                <span className={`text-xs ${selectedResolution === resKey ? 'text-orange-400' : 'text-white/50'}`}>
                                    {dims.width}×{dims.height}
                                  </span>
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                  )}
                  </div>
                </div>

                {/* Second Level: Prompt & Generate Image */}
                <div className="space-y-4">
                  <div ref={promptDesktopRef} className="rounded-xl" style={{ borderRadius: '1rem', position: showGuide && guideStep === 9 ? 'relative' : undefined, zIndex: showGuide && guideStep === 9 ? 201 : undefined, boxShadow: showGuide && guideStep === 9 && guideVisible ? '0 0 32px rgba(180, 120, 60, 0.5), 0 0 80px rgba(180, 120, 60, 0.35), 0 0 150px rgba(180, 120, 60, 0.2)' : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4, 0, 0.2, 1)' }}>
                    <label className="text-white/70 text-xs mb-2 block">Prompt:</label>
                    {selectedRoomType === 'Custom' && (
                      <p className="text-orange-400/80 text-[11px] mb-2 flex items-center gap-1">✏️ Describe the room type you want in the prompt below</p>
                    )}
                    <textarea
                      ref={promptRef}
                      value={prompt}
                      onChange={handlePromptChange}
                      placeholder={selectedRoomType === 'Custom' ? 'Describe the room type (e.g. yoga studio, reading nook, wine cellar...)' : 'Additional request'}
                      rows={5}
                      className={`w-full glassmorphic-input rounded-2xl px-4 py-3 text-white bg-white/5 focus:outline-none transition-all placeholder:text-white/30 resize-none overflow-y-auto min-h-[120px] max-h-[300px] ${selectedRoomType === 'Custom' ? 'border-2 border-orange-500/60 focus:border-orange-500' : 'border border-white/10 focus:border-orange-500/50'}`}
                      style={{ height: '120px' }}
                    />
                    <div className="text-right mt-1">
                      <span className={`text-xs ${
                        prompt?.length >= MAX_PROMPT_LENGTH ? 'text-red-400' : 'text-white/50'
                      }`}>
                        {prompt?.length?.toLocaleString()} / {MAX_PROMPT_LENGTH?.toLocaleString()}
                      </span>
                    </div>
                  </div>

                  <div ref={generateDesktopRef} className="rounded-xl" style={{ borderRadius: '1rem', position: showGuide && guideStep === 10 ? 'relative' : undefined, zIndex: showGuide && guideStep === 10 ? 201 : undefined, boxShadow: showGuide && guideStep === 10 && guideVisible ? '0 0 32px rgba(180, 120, 60, 0.5), 0 0 80px rgba(180, 120, 60, 0.35), 0 0 150px rgba(180, 120, 60, 0.2)' : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4, 0, 0.2, 1)' }}>
                  <button
                    onClick={handleGenerate}
                    disabled={isGenerating}
                    className="w-full glassmorphic-button rounded-2xl py-3 text-white font-medium flex items-center justify-center gap-2 hover:scale-[1.02] transition-transform disabled:opacity-50 disabled:cursor-not-allowed disabled:scale-100">
                    <Icon name={isGenerating ? 'Loader' : 'Sparkles'} size={18} className={isGenerating ? 'animate-spin' : ''} />
                    {isGenerating ? 'Generating...' : 'Generate Image'}
                  </button>
                  </div>
                  {isGenerating && (
                    <button
                      onClick={handleCancelGeneration}
                      className="w-full glassmorphic-button rounded-2xl py-2 text-sm text-white/70 font-medium flex items-center justify-center gap-2 hover:text-white transition-colors"
                    >
                      <Icon name="X" size={16} />
                      Cancel
                    </button>
                  )}
                </div>

                {/* Cost Text Below Both Levels */}
                <p className="text-white/50 text-xs text-center mt-4">Your request will cost {getModelCreditCost(selectedModel)} credit{getModelCreditCost(selectedModel) !== 1 ? 's' : ''}</p>
              </div>
            </div>
          </div>

          {/* Mobile Layout (visible only on mobile) */}
          <div className="md:hidden space-y-4">
            {/* 1. Main Image Display - First */}
            <div ref={resultMobileRef} className="rounded-2xl" style={{ borderRadius: '1rem', position: showGuide && guideStep === 11 ? 'relative' : undefined, zIndex: showGuide && guideStep === 11 ? 201 : undefined, boxShadow: showGuide && guideStep === 11 && guideVisible ? '0 0 32px rgba(180, 120, 60, 0.5), 0 0 80px rgba(180, 120, 60, 0.35), 0 0 150px rgba(180, 120, 60, 0.2)' : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4, 0, 0.2, 1)' }}>
            <div className="glassmorphic-card rounded-2xl p-3 relative">
              <div className="flex justify-between items-center mb-2 px-1">
                <span className="text-white/70 text-xs"></span>
                <span className="text-white/70 text-xs">Result</span>
              </div>
              <div className="relative rounded-xl overflow-hidden bg-black/40 h-[280px]">
                {isGenerating ? (
                  <div className="w-full h-full flex items-center justify-center">
                    <div className="text-center">
                      <Icon name="Loader" size={36} className="text-orange-500 mx-auto mb-2 animate-spin" />
                      <p className="text-white text-xs font-medium">Generating...</p>
                    </div>
                  </div>
                ) : currentImage ? (
                  <img
                    src={currentImage}
                    alt="Generated interior render"
                    className="w-full h-full object-contain" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <div className="text-center">
                      <p className="text-white/50 text-xs">No image generated yet</p>
                      <p className="text-white/30 text-xs mt-1">Upload images and click Generate</p>
                    </div>
                  </div>
                )}

                {/* Error overlay for mobile */}
                {generationError && !isGenerating && (
                  <div className="absolute inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center">
                    <div className="text-center px-4">
                      <Icon name="AlertCircle" size={36} className="text-red-500 mx-auto mb-2" />
                      <p className="text-white text-xs font-medium">Failed</p>
                    </div>
                  </div>
                )}

                {currentImage && (
                  <div className="absolute bottom-3 right-3 flex gap-1.5">
                    <button
                      onClick={handleDownload}
                      disabled={buttonLoading === 'download'}
                      className="glassmorphic-icon-button rounded-lg p-2 hover:scale-105 transition-transform"
                      title="Download">
                      {buttonLoading === 'download' ? (
                        <Icon name="Loader" size={16} className="text-white animate-spin" />
                      ) : (
                        <Icon name="Download" size={16} className="text-white" />
                      )}
                    </button>
                    <button
                      className="glassmorphic-icon-button rounded-lg p-2 hover:scale-105 transition-transform"
                      title="Delete">
                      <Icon name="Trash2" size={16} className="text-white" />
                    </button>
                    <button
                      className="glassmorphic-icon-button rounded-lg p-2 hover:scale-105 transition-transform"
                      title="Recreate">
                      <Icon name="RefreshCw" size={16} className="text-white" />
                    </button>
                  </div>
                )}
              </div>
            </div>
            </div>

            {/* Upload Counter - Mobile */}
            <div className="text-left">
              <span className={`text-sm font-medium ${totalUploads >= MAX_UPLOADS ? "text-red-400" : "text-white/70"}`}>
                {totalUploads} / {MAX_UPLOADS} images uploaded
              </span>
            </div>

            {/* 2. Sketch and Mood Uploads - Stacked */}
            <div className="grid grid-cols-1 gap-3">
              {/* Sketch(es) Upload */}
              <div ref={sketchUploadMobileRef} className="rounded-xl" style={{ borderRadius: '1rem', position: showGuide && guideStep === 1 ? 'relative' : undefined, zIndex: showGuide && guideStep === 1 ? 201 : undefined, boxShadow: showGuide && guideStep === 1 && guideVisible ? '0 0 32px rgba(180, 120, 60, 0.5), 0 0 80px rgba(180, 120, 60, 0.35), 0 0 150px rgba(180, 120, 60, 0.2)' : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4, 0, 0.2, 1)' }}>
              <div className="glassmorphic-card rounded-2xl p-3 border border-transparent transition-all duration-200"
                onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'copy'; e.currentTarget.classList.add('border-orange-500/50'); }}
                onDragLeave={(e) => { e.currentTarget.classList.remove('border-orange-500/50'); }}
                onDrop={(e) => {
                  e.preventDefault();
                  e.currentTarget.classList.remove('border-orange-500/50');
                  const imageUrl = e.dataTransfer.getData('imageUrl') || e.dataTransfer.getData('text/plain');
                  if (imageUrl) {
                    if (totalUploads < MAX_UPLOADS) {
                      setSketchImages((prev) => prev.includes(imageUrl) ? prev : [...prev, imageUrl]);
                    }
                    return;
                  }
                }}>
                <h3 className="text-white text-xs font-medium mb-2">Sketch(es)<span className="text-red-500">*</span></h3>
                <label className={`block ${totalUploads >= MAX_UPLOADS ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}>
                  <input
                    type="file"
                    multiple
                    accept="image/png,image/jpeg"
                    onChange={handleSketchUpload}
                    disabled={totalUploads >= MAX_UPLOADS}
                    className="hidden" />

                  <div className={`glassmorphic-upload-zone rounded-xl p-4 text-center border-2 border-dashed transition-all duration-300 ${totalUploads >= MAX_UPLOADS ? 'border-white/10' : 'border-white/20 hover:border-orange-500/40'}`}>
                    <Icon name="Image" size={24} className="text-orange-500 mx-auto mb-1.5" />
                    <p className="text-white/70 text-[10px]">Upload PNG or JPG</p>
                  </div>
                </label>
                {sketchImages?.length > 0 &&
                <div className="mt-2 flex flex-wrap gap-1.5">
                    {sketchImages?.map((img, idx) =>
                  <UploadedImageThumb key={idx} src={img} alt={`Sketch ${idx + 1}`} onRemove={() => handleRemoveSketchImage(idx)} imgClass="max-w-[60px] max-h-[45px] object-contain rounded-lg border border-white/10" height="h-auto" previewWidth="w-[200px]" />
                  )}
                  </div>
                }
              </div>
              </div>

              {/* Mood image(s) Upload */}
              <div ref={moodUploadMobileRef} className="rounded-xl" style={{ borderRadius: '1rem', position: showGuide && guideStep === 2 ? 'relative' : undefined, zIndex: showGuide && guideStep === 2 ? 201 : undefined, boxShadow: showGuide && guideStep === 2 && guideVisible ? '0 0 32px rgba(180, 120, 60, 0.5), 0 0 80px rgba(180, 120, 60, 0.35), 0 0 150px rgba(180, 120, 60, 0.2)' : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4, 0, 0.2, 1)' }}>
              <div className="glassmorphic-card rounded-2xl p-3 border border-transparent transition-all duration-200"
                onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'copy'; e.currentTarget.classList.add('border-orange-500/50'); }}
                onDragLeave={(e) => { e.currentTarget.classList.remove('border-orange-500/50'); }}
                onDrop={(e) => {
                  e.preventDefault();
                  e.currentTarget.classList.remove('border-orange-500/50');
                  const imageUrl = e.dataTransfer.getData('imageUrl') || e.dataTransfer.getData('text/plain');
                  if (imageUrl) {
                    if (totalUploads < MAX_UPLOADS) {
                      setMoodImages((prev) => prev.includes(imageUrl) ? prev : [...prev, imageUrl]);
                    }
                    return;
                  }
                }}>
                <h3 className="text-white text-xs font-medium mb-2">Mood image(s)<span className="text-red-500">*</span></h3>
                <label className={`block ${totalUploads >= MAX_UPLOADS ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}>
                  <input
                    type="file"
                    multiple
                    accept="image/png,image/jpeg"
                    onChange={handleMoodUpload}
                    disabled={totalUploads >= MAX_UPLOADS}
                    className="hidden" />

                  <div className={`glassmorphic-upload-zone rounded-xl p-4 text-center border-2 border-dashed transition-all duration-300 ${totalUploads >= MAX_UPLOADS ? 'border-white/10' : 'border-white/20 hover:border-orange-500/40'}`}>
                    <Icon name="Image" size={24} className="text-orange-500 mx-auto mb-1.5" />
                    <p className="text-white/70 text-[10px]">Upload PNG or JPG</p>
                  </div>
                </label>
                {moodImages?.length > 0 &&
                <div className="mt-2 flex flex-wrap gap-1.5">
                    {moodImages?.map((img, idx) =>
                  <UploadedImageThumb key={idx} src={img} alt={`Mood ${idx + 1}`} onRemove={() => handleRemoveMoodImage(idx)} imgClass="max-w-[60px] max-h-[45px] object-contain rounded-lg border border-white/10" height="h-auto" previewWidth="w-[200px]" />
                  )}
                  </div>
                }
              </div>
              </div>
            </div>

            {/* 3. Model Selection, Aspect Ratio, Prompt, Generate Button - Third */}
            <div className="glassmorphic-card rounded-2xl p-4 space-y-3">
              {/* Sketch Optimization Section - Mobile */}
              <div className="space-y-3 pb-3 border-b border-white/10">
                <h3 className="text-white text-xs font-semibold">Sketch optimization</h3>

                {/* Room Type Dropdown */}
                <div ref={roomTypeMobileRef} className="rounded-xl" style={{ borderRadius: '0.75rem', position: showGuide && guideStep === 3 ? 'relative' : undefined, zIndex: showGuide && guideStep === 3 ? 201 : undefined, boxShadow: showGuide && guideStep === 3 && guideVisible ? '0 0 32px rgba(180, 120, 60, 0.5), 0 0 80px rgba(180, 120, 60, 0.35), 0 0 150px rgba(180, 120, 60, 0.2)' : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4, 0, 0.2, 1)' }}>
                <RoomTypeDropdown
                  value={selectedRoomType}
                  onChange={(val) => {
                    setSelectedRoomType(val);
                    if (val === 'Custom') {
                      setTimeout(() => {
                        if (promptRef?.current) {
                          promptRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
                          promptRef.current.focus();
                        }
                      }, 80);
                    }
                  }}
                  mobile
                  showCustomOption
                />
                </div>

                {/* Material Uploads Grid */}
                <div ref={materialsMobileRef} className="rounded-xl" style={{ position: showGuide && guideStep === 4 ? 'relative' : undefined, zIndex: showGuide && guideStep === 4 ? 201 : undefined, boxShadow: showGuide && guideStep === 4 && guideVisible ? '0 0 32px rgba(180, 120, 60, 0.5), 0 0 80px rgba(180, 120, 60, 0.35), 0 0 150px rgba(180, 120, 60, 0.2)' : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4, 0, 0.2, 1)' }}>
                <div className="grid grid-cols-2 gap-2">
                  {/* Walls */}
                  <div className="border border-transparent transition-all duration-200 rounded-lg"
                    onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'copy'; e.currentTarget.classList.add('border-orange-500/50'); }}
                    onDragLeave={(e) => { e.currentTarget.classList.remove('border-orange-500/50'); }}
                    onDrop={(e) => {
                      e.preventDefault();
                      e.currentTarget.classList.remove('border-orange-500/50');
                      const imageUrl = e.dataTransfer.getData('imageUrl') || e.dataTransfer.getData('text/plain');
                      if (imageUrl) {
                        if (totalUploads < MAX_UPLOADS) {
                          setWallsMaterial((prev) => prev.includes(imageUrl) ? prev : [...prev, imageUrl]);
                        }
                        return;
                      }
                    }}>
                    <label className="text-white/70 text-[10px] mb-1 block">Walls</label>
                    <label className={`block ${totalUploads >= MAX_UPLOADS ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}>
                      <input
                        type="file"
                        multiple
                        accept="image/png,image/jpeg"
                        onChange={(e) => handleMaterialUpload(e, 'walls')}
                        disabled={totalUploads >= MAX_UPLOADS}
                        className="hidden" />

                      <div className={`glassmorphic-upload-zone rounded-lg p-2 text-center border border-dashed transition-all ${totalUploads >= MAX_UPLOADS ? 'border-white/10' : 'border-white/20 hover:border-orange-500/40'}`}>
                        <Icon name="Upload" size={16} className="text-orange-500 mx-auto mb-0.5" />
                        <p className="text-white/70 text-[9px]">Upload</p>
                      </div>
                    </label>
                    {wallsMaterial?.length > 0 &&
                    <div className="mt-1 flex gap-1 overflow-x-auto">
                        {wallsMaterial?.map((img, idx) =>
                      <UploadedImageThumb key={idx} src={img} alt={`Walls ${idx + 1}`} onRemove={() => handleRemoveMaterial(idx, 'walls')} imgClass="max-w-[60px] max-h-[45px] object-contain rounded-lg border border-white/10" height="h-auto" previewWidth="w-[200px]" />
                      )}
                      </div>
                    }
                  </div>

                  {/* Sofas/chairs */}
                  <div className="border border-transparent transition-all duration-200 rounded-lg"
                    onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'copy'; e.currentTarget.classList.add('border-orange-500/50'); }}
                    onDragLeave={(e) => { e.currentTarget.classList.remove('border-orange-500/50'); }}
                    onDrop={(e) => {
                      e.preventDefault();
                      e.currentTarget.classList.remove('border-orange-500/50');
                      const imageUrl = e.dataTransfer.getData('imageUrl') || e.dataTransfer.getData('text/plain');
                      if (imageUrl) {
                        if (totalUploads < MAX_UPLOADS) {
                          setSofasMaterial((prev) => prev.includes(imageUrl) ? prev : [...prev, imageUrl]);
                        }
                        return;
                      }
                    }}>
                    <label className="text-white/70 text-[10px] mb-1 block">Sofas / chairs</label>
                    <label className={`block ${totalUploads >= MAX_UPLOADS ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}>
                      <input
                        type="file"
                        multiple
                        accept="image/png,image/jpeg"
                        onChange={(e) => handleMaterialUpload(e, 'sofas')}
                        disabled={totalUploads >= MAX_UPLOADS}
                        className="hidden" />

                      <div className={`glassmorphic-upload-zone rounded-lg p-2 text-center border border-dashed transition-all ${totalUploads >= MAX_UPLOADS ? 'border-white/10' : 'border-white/20 hover:border-orange-500/40'}`}>
                        <Icon name="Upload" size={16} className="text-orange-500 mx-auto mb-0.5" />
                        <p className="text-white/70 text-[9px]">Upload</p>
                      </div>
                    </label>
                    {sofasMaterial?.length > 0 &&
                    <div className="mt-1 flex gap-1 overflow-x-auto">
                        {sofasMaterial?.map((img, idx) =>
                      <UploadedImageThumb key={idx} src={img} alt={`Sofas ${idx + 1}`} onRemove={() => handleRemoveMaterial(idx, 'sofas')} imgClass="max-w-[60px] max-h-[45px] object-contain rounded-lg border border-white/10" height="h-auto" previewWidth="w-[200px]" />
                      )}
                      </div>
                    }
                  </div>

                  {/* Ceiling */}
                  <div className="border border-transparent transition-all duration-200 rounded-lg"
                    onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'copy'; e.currentTarget.classList.add('border-orange-500/50'); }}
                    onDragLeave={(e) => { e.currentTarget.classList.remove('border-orange-500/50'); }}
                    onDrop={(e) => {
                      e.preventDefault();
                      e.currentTarget.classList.remove('border-orange-500/50');
                      const imageUrl = e.dataTransfer.getData('imageUrl') || e.dataTransfer.getData('text/plain');
                      if (imageUrl) {
                        if (totalUploads < MAX_UPLOADS) {
                          setCeilingMaterial((prev) => prev.includes(imageUrl) ? prev : [...prev, imageUrl]);
                        }
                        return;
                      }
                    }}>
                    <label className="text-white/70 text-[10px] mb-1 block">Ceiling</label>
                    <label className={`block ${totalUploads >= MAX_UPLOADS ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}>
                      <input
                        type="file"
                        multiple
                        accept="image/png,image/jpeg"
                        onChange={(e) => handleMaterialUpload(e, 'ceiling')}
                        disabled={totalUploads >= MAX_UPLOADS}
                        className="hidden" />

                      <div className={`glassmorphic-upload-zone rounded-lg p-2 text-center border border-dashed transition-all ${totalUploads >= MAX_UPLOADS ? 'border-white/10' : 'border-white/20 hover:border-orange-500/40'}`}>
                        <Icon name="Upload" size={16} className="text-orange-500 mx-auto mb-0.5" />
                        <p className="text-white/70 text-[9px]">Upload</p>
                      </div>
                    </label>
                    {ceilingMaterial?.length > 0 &&
                    <div className="mt-1 flex gap-1 overflow-x-auto">
                        {ceilingMaterial?.map((img, idx) =>
                      <UploadedImageThumb key={idx} src={img} alt={`Ceiling ${idx + 1}`} onRemove={() => handleRemoveMaterial(idx, 'ceiling')} imgClass="max-w-[60px] max-h-[45px] object-contain rounded-lg border border-white/10" height="h-auto" previewWidth="w-[200px]" />
                      )}
                      </div>
                    }
                  </div>

                  {/* Floor */}
                  <div className="border border-transparent transition-all duration-200 rounded-lg"
                    onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'copy'; e.currentTarget.classList.add('border-orange-500/50'); }}
                    onDragLeave={(e) => { e.currentTarget.classList.remove('border-orange-500/50'); }}
                    onDrop={(e) => {
                      e.preventDefault();
                      e.currentTarget.classList.remove('border-orange-500/50');
                      const imageUrl = e.dataTransfer.getData('imageUrl') || e.dataTransfer.getData('text/plain');
                      if (imageUrl) {
                        if (totalUploads < MAX_UPLOADS) {
                          setFloorMaterial((prev) => prev.includes(imageUrl) ? prev : [...prev, imageUrl]);
                        }
                        return;
                      }
                    }}>
                    <label className="text-white/70 text-[10px] mb-1 block">Floor</label>
                    <label className={`block ${totalUploads >= MAX_UPLOADS ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}>
                      <input
                        type="file"
                        multiple
                        accept="image/png,image/jpeg"
                        onChange={(e) => handleMaterialUpload(e, 'floor')}
                        disabled={totalUploads >= MAX_UPLOADS}
                        className="hidden" />

                      <div className={`glassmorphic-upload-zone rounded-lg p-2 text-center border border-dashed transition-all ${totalUploads >= MAX_UPLOADS ? 'border-white/10' : 'border-white/20 hover:border-orange-500/40'}`}>
                        <Icon name="Upload" size={16} className="text-orange-500 mx-auto mb-0.5" />
                        <p className="text-white/70 text-[9px]">Upload</p>
                      </div>
                    </label>
                    {floorMaterial?.length > 0 &&
                    <div className="mt-1 flex gap-1 overflow-x-auto">
                        {floorMaterial?.map((img, idx) =>
                      <UploadedImageThumb key={idx} src={img} alt={`Floor ${idx + 1}`} onRemove={() => handleRemoveMaterial(idx, 'floor')} imgClass="max-w-[60px] max-h-[45px] object-contain rounded-lg border border-white/10" height="h-auto" previewWidth="w-[200px]" />
                      )}
                      </div>
                    }
                  </div>
                </div>
                </div>

              </div>

              <div ref={modelSelectionMobileRef} className="rounded-xl" style={{ position: showGuide && guideStep === 6 ? 'relative' : undefined, zIndex: showGuide && guideStep === 6 ? 201 : undefined, boxShadow: showGuide && guideStep === 6 && guideVisible ? '0 0 32px rgba(180, 120, 60, 0.5), 0 0 80px rgba(180, 120, 60, 0.35), 0 0 150px rgba(180, 120, 60, 0.2)' : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4, 0, 0.2, 1)' }}>
                <ModelSelectionDropdown
                  value={selectedModel}
                  onChange={setSelectedModel}
                  mobile
                />
              </div>

              <div ref={aspectRatioGlowMobileRef} className="rounded-xl" style={{ position: showGuide && guideStep === 7 ? 'relative' : undefined, zIndex: showGuide && guideStep === 7 ? 201 : undefined, boxShadow: showGuide && guideStep === 7 && guideVisible ? '0 0 32px rgba(180, 120, 60, 0.5), 0 0 80px rgba(180, 120, 60, 0.35), 0 0 150px rgba(180, 120, 60, 0.2)' : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4, 0, 0.2, 1)' }}>
              <div className="relative" ref={aspectRatioMobileRef}>
                <label className="text-white/70 text-xs mb-1.5 block">Aspect Ratio</label>
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setIsAspectRatioOpen(!isAspectRatioOpen)}
                    className="w-full glassmorphic-select rounded-xl px-3 py-2.5 bg-white/5 border border-white/10 flex items-center justify-between hover:border-orange-500/30 transition-all"
                  >
                    <div className="flex items-center gap-2">
                      <AspectRatioIcon ratio={selectedImageSize} size={14} className="text-orange-500" />
                      <span className="text-white text-xs">{selectedImageSize}</span>
                    </div>
                    <Icon name={isAspectRatioOpen ? 'ChevronUp' : 'ChevronDown'} size={14} className="text-white/50" />
                  </button>
                  {isAspectRatioOpen && (
                    <div
                      className="absolute z-50 w-full bottom-full mb-2 rounded-xl border border-white/10 shadow-2xl overflow-hidden max-h-60 overflow-y-auto"
                      style={{ backgroundColor: 'rgba(0, 0, 0, 0.95)', backdropFilter: 'blur(8px)' }}
                      onMouseDown={(e) => e.preventDefault()}
                    >
                      {aspectRatios?.map((ratio) => (
                        <button
                          key={ratio?.value}
                          type="button"
                          onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); setSelectedImageSize(ratio?.value); setIsAspectRatioOpen(false); }}
                          className={`w-full flex items-center gap-2 px-3 py-2.5 transition-all border-b border-white/5 last:border-b-0 ${
                            selectedImageSize === ratio?.value
                              ? 'bg-orange-500/20 hover:bg-orange-500/30'
                              : 'hover:bg-white/5'
                          }`}
                        >
                          <AspectRatioIcon
                            ratio={ratio?.value}
                            size={14}
                            className={selectedImageSize === ratio?.value ? 'text-orange-500' : 'text-white/50'}
                          />
                          <span className={`text-xs ${selectedImageSize === ratio?.value ? 'text-white font-medium' : 'text-white/70'}`}>
                            {ratio?.label}
                          </span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              </div>

              {/* Resolution Dropdown - mobile */}
              <div ref={resolutionGlowMobileRef} className="rounded-xl" style={{ position: showGuide && guideStep === 8 ? 'relative' : undefined, zIndex: showGuide && guideStep === 8 ? 201 : undefined, boxShadow: showGuide && guideStep === 8 && guideVisible ? '0 0 32px rgba(180, 120, 60, 0.5), 0 0 80px rgba(180, 120, 60, 0.35), 0 0 150px rgba(180, 120, 60, 0.2)' : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4, 0, 0.2, 1)' }}>
              {!isFluxModel(selectedModel) && (
              <div className="relative" ref={resolutionMobileRef}>
                <label className="text-white/70 text-xs mb-1.5 block">Resolution</label>
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setIsResolutionOpen(!isResolutionOpen)}
                    className={`w-full glassmorphic-select rounded-xl px-3 py-2.5 bg-white/5 border border-white/10 flex items-center gap-3 justify-between hover:border-orange-500/30 transition-all`}
                  >
                    <span className="text-white text-xs font-medium">{selectedResolution}</span>
                    <div className="flex items-center gap-1.5">
                      <span className="text-white/50 text-xs">
                          {getResolutionDimensions(selectedResolution).width}×{getResolutionDimensions(selectedResolution).height}
                        </span>
                      <Icon name={isResolutionOpen ? 'ChevronUp' : 'ChevronDown'} size={14} className="text-white/50" />
                    </div>
                  </button>
                  {isResolutionOpen && (
                    <div
                      className="absolute z-50 w-full bottom-full mb-2 rounded-xl border border-white/10 shadow-2xl overflow-hidden"
                      style={{ backgroundColor: 'rgba(0, 0, 0, 0.95)', backdropFilter: 'blur(8px)' }}
                      onMouseDown={(e) => e.preventDefault()}
                    >
                      {RESOLUTION_OPTIONS.map((resKey) => {
                        const dims = getResolutionDimensions(resKey);
                        return (
                          <button
                            key={resKey}
                            type="button"
                            onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); setSelectedResolution(resKey); setIsResolutionOpen(false); }}
                            className={`w-full flex items-center justify-between px-3 py-2.5 transition-all border-b border-white/5 last:border-b-0 ${
                              selectedResolution === resKey
                                ? 'bg-orange-500/20 hover:bg-orange-500/30'
                                : 'hover:bg-white/5'
                            }`}
                          >
                            <span className={`text-xs ${selectedResolution === resKey ? 'text-white font-medium' : 'text-white/70'}`}>
                              {resKey}
                            </span>
                            <span className={`text-xs ${selectedResolution === resKey ? 'text-orange-400' : 'text-white/50'}`}>
                                {dims.width}×{dims.height}
                              </span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
              )}
              </div>

              <div ref={promptMobileRef} className="rounded-xl mb-4" style={{ position: showGuide && guideStep === 9 ? 'relative' : undefined, zIndex: showGuide && guideStep === 9 ? 201 : undefined, boxShadow: showGuide && guideStep === 9 && guideVisible ? '0 0 32px rgba(180, 120, 60, 0.5), 0 0 80px rgba(180, 120, 60, 0.35), 0 0 150px rgba(180, 120, 60, 0.2)' : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4, 0, 0.2, 1)' }}>
                <label className="block text-white text-xs font-medium mb-2">Prompt:</label>
                <textarea
                  ref={promptRef}
                  value={prompt}
                  onChange={handlePromptChange}
                  placeholder="Additional request"
                  rows={5}
                  className="w-full glassmorphic-input rounded-xl px-4 py-3 text-sm text-white bg-white/5 border border-white/10 focus:border-orange-500/50 focus:outline-none transition-all placeholder:text-white/30 resize-none overflow-y-auto min-h-[120px] max-h-[300px]"
                  style={{ height: '120px' }}
                />
                <div className="text-right mt-1">
                  <span className={`text-xs ${
                    prompt?.length >= MAX_PROMPT_LENGTH ? 'text-red-400' : 'text-white/50'
                  }`}>
                    {prompt?.length?.toLocaleString()} / {MAX_PROMPT_LENGTH?.toLocaleString()}
                  </span>
                </div>
              </div>

              <div ref={generateMobileRef} className="rounded-xl" style={{ position: showGuide && guideStep === 10 ? 'relative' : undefined, zIndex: showGuide && guideStep === 10 ? 201 : undefined, boxShadow: showGuide && guideStep === 10 && guideVisible ? '0 0 32px rgba(180, 120, 60, 0.5), 0 0 80px rgba(180, 120, 60, 0.35), 0 0 150px rgba(180, 120, 60, 0.2)' : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4, 0, 0.2, 1)' }}>
              <button
                onClick={handleGenerate}
                disabled={isGenerating}
                className="w-full glassmorphic-button rounded-xl py-2.5 text-sm text-white font-medium flex items-center justify-center gap-2 hover:scale-[1.02] transition-transform disabled:opacity-50 disabled:cursor-not-allowed disabled:scale-100">
                <Icon name={isGenerating ? 'Loader' : 'Sparkles'} size={16} className={isGenerating ? 'animate-spin' : ''} />
                {isGenerating ? 'Generating...' : 'Generate Image'}
              </button>
              </div>
              {isGenerating && (
                <button
                  onClick={handleCancelGeneration}
                  className="w-full glassmorphic-button rounded-xl py-2 text-sm text-white/70 font-medium flex items-center justify-center gap-2 hover:text-white transition-colors"
                >
                  <Icon name="X" size={16} />
                  Cancel
                </button>
              )}

              <p className="text-white/50 text-[10px] text-center">Your request will cost {getModelCreditCost(selectedModel)} credit{getModelCreditCost(selectedModel) !== 1 ? 's' : ''}</p>
            </div>

            {/* Asset Library Panel - Mobile */}
            <div ref={assetLibraryMobileRef} className="rounded-2xl" style={{ position: showGuide && guideStep === 5 ? 'relative' : undefined, zIndex: showGuide && guideStep === 5 ? 201 : undefined, boxShadow: showGuide && guideStep === 5 && guideVisible ? '0 0 32px rgba(180, 120, 60, 0.5), 0 0 80px rgba(180, 120, 60, 0.35), 0 0 150px rgba(180, 120, 60, 0.2)' : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4, 0, 0.2, 1)' }}>
            <AssetLibraryPanel
              libraryAssets={libraryAssets}
              setLibraryAssets={setLibraryAssets}
              generatedImages={sessionLibraryImages}
              setGeneratedImages={setSessionLibraryImages}
              currentProjectId={currentProjectId}
              onImageDeleted={(remaining) => {
                if (!isGenerating) setCurrentImage(remaining[0] || null);
              }}
            />
            </div>
          </div>
        </div>
      </main>
      {/* Virtual Tool Guide Overlay */}
      {showGuide && (
        <>
        <div
          className="fixed inset-0 z-[200]"
          onClick={closeGuide}
        >
          <div
            className="absolute inset-0 transition-opacity duration-600 ease-[cubic-bezier(0.4,0,0.2,1)]"
            style={{ background: 'rgba(0, 0, 0, 0.30)', opacity: guideVisible ? 1 : 0 }}
          />
        </div>

        {/* Tooltip bubble */}
        <div
          className="fixed z-[9999] w-[280px] md:w-[300px] pointer-events-none"
          style={(() => {
            if (guideAnimating) {
              return {
                ...guideBubblePosRef.current,
                opacity: 0,
                transform: 'translateY(-50%) translateX(-24px)',
                transition: 'opacity 0.6s cubic-bezier(0.4, 0, 0.2, 1), transform 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
              };
            }
            const desktopRefs = [sketchUploadDesktopRef.current, moodUploadDesktopRef.current, roomTypeDesktopRef.current, materialsDesktopRef.current, assetLibraryDesktopRef.current, modelSelectionDesktopRef.current, aspectRatioGlowDesktopRef.current, resolutionGlowDesktopRef.current, promptDesktopRef.current, generateDesktopRef.current, resultDesktopRef.current];
            const mobileRefs = [sketchUploadMobileRef.current, moodUploadMobileRef.current, roomTypeMobileRef.current, materialsMobileRef.current, assetLibraryMobileRef.current, modelSelectionMobileRef.current, aspectRatioGlowMobileRef.current, resolutionGlowMobileRef.current, promptMobileRef.current, generateMobileRef.current, resultMobileRef.current];
            const desktopRef = desktopRefs[guideStep - 1];
            const mobileRef = mobileRefs[guideStep - 1];
            const ref = window.innerWidth >= 768 ? desktopRef : mobileRef;
            if (!ref) return { top: '50%', left: '50%' };
            const rect = ref.getBoundingClientRect();
            const bubbleWidth = window.innerWidth >= 768 ? 300 : 280;
            if (guideStep >= 6 && guideStep <= 11) {
              const pos = { top: rect.top + rect.height / 2, left: rect.left - bubbleWidth - 32 };
              guideBubblePosRef.current = pos;
              return {
                ...pos,
                opacity: guideVisible ? 1 : 0,
                transform: guideVisible ? 'translateY(-50%) translateX(0px)' : 'translateY(-50%) translateX(24px)',
                transition: 'opacity 0.6s cubic-bezier(0.4, 0, 0.2, 1), transform 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
              };
            }
            const pos = { top: rect.top + rect.height / 2, left: rect.right + 32 };
            guideBubblePosRef.current = pos;
            return {
              ...pos,
              opacity: guideVisible ? 1 : 0,
              transform: guideVisible ? 'translateY(-50%) translateX(0px)' : 'translateY(-50%) translateX(-24px)',
              transition: 'opacity 0.6s cubic-bezier(0.4, 0, 0.2, 1), transform 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
            };
          })()}
        >
          <div className="pointer-events-auto">
            {/* Arrow - conditional direction */}
            {(guideStep >= 6 && guideStep <= 11) ? (
              <>
                <div
                  className="absolute top-1/2 -right-2 -translate-y-1/2 w-0 h-0"
                  style={{
                    borderTop: '10px solid transparent',
                    borderBottom: '10px solid transparent',
                    borderLeft: '10px solid rgba(254, 131, 56, 0.3)',
                    opacity: guideVisible ? 1 : 0,
                    transition: 'opacity 0.5s cubic-bezier(0.4, 0, 0.2, 1) 0.15s',
                  }}
                />
                <div
                  className="absolute top-1/2 -right-[6px] -translate-y-1/2 w-0 h-0"
                  style={{
                    borderTop: '9px solid transparent',
                    borderBottom: '9px solid transparent',
                    borderLeft: '9px solid rgba(30, 30, 30, 0.95)',
                    opacity: guideVisible ? 1 : 0,
                    transition: 'opacity 0.5s cubic-bezier(0.4, 0, 0.2, 1) 0.15s',
                  }}
                />
              </>
            ) : (
              <>
                <div
                  className="absolute top-1/2 -left-2 -translate-y-1/2 w-0 h-0"
                  style={{
                    borderTop: '10px solid transparent',
                    borderBottom: '10px solid transparent',
                    borderRight: '10px solid rgba(254, 131, 56, 0.3)',
                    opacity: guideVisible ? 1 : 0,
                    transition: 'opacity 0.5s cubic-bezier(0.4, 0, 0.2, 1) 0.15s',
                  }}
                />
                <div
                  className="absolute top-1/2 -left-[6px] -translate-y-1/2 w-0 h-0"
                  style={{
                    borderTop: '9px solid transparent',
                    borderBottom: '9px solid transparent',
                    borderRight: '9px solid rgba(30, 30, 30, 0.95)',
                    opacity: guideVisible ? 1 : 0,
                    transition: 'opacity 0.5s cubic-bezier(0.4, 0, 0.2, 1) 0.15s',
                  }}
                />
              </>
            )}
            <div
              className="rounded-2xl p-5 text-center"
              style={{ background: 'rgb(24, 24, 24)', border: '1px solid rgba(254, 131, 56, 0.3)', boxShadow: '0 8px 32px rgba(0,0,0,0.5), 0 0 20px rgba(254, 131, 56, 0.1)' }}
            >
              <div
                className="flex items-center justify-center gap-2 mb-2"
                style={{
                  opacity: guideVisible ? 1 : 0,
                  transform: guideVisible ? 'translateY(0)' : 'translateY(8px)',
                  transition: 'opacity 0.5s cubic-bezier(0.4, 0, 0.2, 1) 0.1s, transform 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94) 0.1s',
                }}
              >
                <Icon name={[null, "Upload", "LayoutGrid", "Home", "Layers", "FolderOpen", "Cpu", "RectangleHorizontal", "SlidersHorizontal", "MessageSquare", "Sparkles", "Monitor"][guideStep] || "Settings"} size={18} className="text-white/50" />
                <span className="text-sm font-semibold text-white">Step {guideStep}</span>
              </div>
              <p
                className="text-sm text-white/80 leading-relaxed"
                style={{
                  opacity: guideVisible ? 1 : 0,
                  transform: guideVisible ? 'translateY(0)' : 'translateY(8px)',
                  transition: 'opacity 0.5s cubic-bezier(0.4, 0, 0.2, 1) 0.2s, transform 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94) 0.2s',
                }}
              >
                {guideStep === 1 ? (
                  <>Upload your hand-drawn or digital interior sketch. This is the spatial and compositional foundation from which the AI generates a fully rendered interior. Without a sketch reference, the image generation will not start.</>
                ) : guideStep === 2 ? (
                  <>Upload reference images that define the desired interior style, color palette, and atmosphere. The AI will apply this visual direction onto your sketch to produce a photorealistic render. Without a mood reference, the image generation will not start.</>
                ) : guideStep === 3 ? (
                  <>Selection which allows you to choose the <span className="text-white font-medium italic">type of room</span> you want to generate. Located within Sketch optimization, it helps the AI better interpret your sketch and apply context-appropriate furnishing and detailing.</>
                ) : guideStep === 4 ? (
                  <>A set of <span className="text-white font-medium italic">material controls</span> that allow you to fine-tune specific surfaces in your render. All material uploads are optional. When provided, they override the general mood direction for that specific surface.</>
                ) : guideStep === 5 ? (
                  <>Generated images which you chose to store inside Roomwork.ai can be <span className="text-white font-medium italic">dragged and dropped</span> into 'Sketch(es)' or 'Mood image(s)'.</>
                ) : guideStep === 6 ? (
                  <>Image generating <span className="text-white font-medium italic">Models</span> which create images. Different models allow you to explore different results.</>
                ) : guideStep === 7 ? (
                  <><span className="text-white font-medium italic">Aspect Ratio</span> — Image ratio: vertical or horizontal (choose the precise ratio according to the dropdown menu icons). Different ratios allow you to explore different room compositions.</>
                ) : guideStep === 8 ? (
                  <>A dropdown selector that allows you to choose the <span className="text-white font-medium italic">output image resolution</span> for your generation. Higher resolution produces a more detailed and sharper image, suitable for professional presentations or print use.</>
                ) : guideStep === 9 ? (
                  <>An optional text field for <span className="text-white font-medium italic">additional instructions</span>: specify design details, lighting conditions, or any direction not covered by your sketch and mood references.</>
                ) : guideStep === 10 ? (
                  <>Hit '<span className="text-white font-medium italic">Generate Image</span>' once you're ready! Choose only the fields that are relevant to your needs.</>
                ) : guideStep === 11 ? (
                  <>Receive the generated image in the '<span className="text-white font-medium italic">Result</span>' window. Use the buttons in the bottom right corner of the Result window to download, delete, or regenerate the image using the exact same settings.</>
                ) : null}
              </p>
              <button
                onClick={() => guideStep < totalGuideSteps ? nextGuideStep() : closeGuide()}
                className="mt-4 px-5 py-2 glassmorphic-button rounded-xl text-white text-xs font-medium hover:scale-[1.02]"
                style={{
                  opacity: guideVisible ? 1 : 0,
                  transform: guideVisible ? 'translateY(0)' : 'translateY(8px)',
                  transition: 'opacity 0.5s cubic-bezier(0.4, 0, 0.2, 1) 0.3s, transform 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94) 0.3s',
                }}
              >
                {guideStep < totalGuideSteps ? 'Next' : 'Got it'}
              </button>
            </div>
          </div>
        </div>
        </>
      )}
    </div>);

};

export default SketchToInteriorRender;
